import React, { ReactNode, memo, useCallback, useMemo } from 'react';
import Dropzone, { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';

import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { SxProps } from '@mui/material';

import { IMediaFile } from '@common/api/media';

import DragTextDefault from './DragText/default';
import DragTextDragOver from './DragText/drag-over';
import UploadedFiles from './UploadedFiles';
import FILES_ACCEPTED_TYPES, { AcceptedTypesKeys } from './constants/types';
import useFileDrop from './hooks/useFileDrop';
import {
	FileUploadErrortext,
	FileUploadFormatsText,
	FileUploadImage,
	FileUploadInsideWrap,
	FileUploadWrap,
	Wrapper,
} from './styled';

export interface FileUploadPropsType extends DropzoneOptions {
	// @TODO: Extend Form.Field props. Not extend DropZoneOptions
	label?: string;
	value?: IMediaFile | IMediaFile[];
	onChange: (newValue?: IMediaFile | IMediaFile[]) => void;
	error?: string;
	acceptFiles: AcceptedTypesKeys | AcceptedTypesKeys[];
	formatsText: string;
	showBgImage?: boolean;
	sx?: SxProps;
	children?: ReactNode;
	defaultValue?: any;
	onFileDelete?: (file: string) => void;
	onFileUpload?: (newValue?: File) => Promise<any> | any;
}

function FileUpload({
	label = 'Uploaded files',
	value,
	onChange,
	error,
	acceptFiles,
	formatsText,
	showBgImage,
	multiple = false,
	maxSize,
	sx,
	children,
	defaultValue = undefined,
	onFileDelete,
	disabled,
	onFileUpload,
	...props
}: FileUploadPropsType) {
	const uploadedFiles = useMemo(
		() =>
			value
				? (Array.isArray(value) ? value : [value]).map(({ url, original_name }: IMediaFile) => ({
						url,
						progress: 100,
						name: original_name || url?.split('/').pop(),
					}))
				: [],
		[value],
	);

	const acceptFilesTypes = useMemo(() => {
		const keysArray = Array.isArray(acceptFiles) ? acceptFiles : [acceptFiles];

		return keysArray.reduce(
			(acc, key) => {
				if (FILES_ACCEPTED_TYPES[key]) {
					return { ...acc, ...FILES_ACCEPTED_TYPES[key] };
				}

				return acc;
			},
			{} as { [x: string]: string[] },
		);
	}, [acceptFiles]);

	const memoizedOnChange = useCallback(onChange, [onChange]);
	const memoizedOnFileUpload = useCallback(onFileUpload, [onFileUpload]);

	const {
		filesOnProgress,
		handleDeleteOnProgress,
		isDragOver,
		onDragEnter,
		onDragLeave,
		onDrop,
		onReject,
		errorType,
	} = useFileDrop({
		multiple,
		value,
		onChange: memoizedOnChange,
		onFileUpload: memoizedOnFileUpload,
	});

	const handleDropRejected = (rejectedFiles: FileRejection[], event: DropEvent) => {
		onReject(rejectedFiles, event, maxSize);
	};

	const handleDrop = (acceptedFiles: File[]) => {
		onDrop(acceptedFiles);
	};

	return (
		<Wrapper disabled={disabled} sx={sx}>
			<FileUploadWrap isDragOver={isDragOver} isError={!!error}>
				<Dropzone
					onDropRejected={handleDropRejected}
					onDrop={handleDrop}
					accept={acceptFilesTypes}
					onDragEnter={onDragEnter}
					onDragLeave={onDragLeave}
					multiple={multiple}
					maxSize={maxSize * 1024 * 1024} // in bytes
					disabled={disabled}
					{...props}
				>
					{({ getRootProps, getInputProps }) => (
						<FileUploadInsideWrap {...getRootProps()}>
							{showBgImage && value && (
								<FileUploadImage src={Array.isArray(value) ? value[0].url : value.url} />
							)}
							<input {...getInputProps()} />
							<InsertDriveFileIcon />
							{isDragOver ? <DragTextDefault /> : <DragTextDragOver />}
							<FileUploadFormatsText>{formatsText}</FileUploadFormatsText>
							{!!error && <FileUploadErrortext>{error}</FileUploadErrortext>}
						</FileUploadInsideWrap>
					)}
				</Dropzone>
			</FileUploadWrap>
			{!!filesOnProgress.length && (
				<UploadedFiles files={filesOnProgress} handleDelete={handleDeleteOnProgress} />
			)}
			<UploadedFiles
				label={label}
				files={uploadedFiles}
				handleDelete={async (file: string) => {
					await onChange(
						Array.isArray(value)
							? value.filter((valueItem: IMediaFile) => valueItem.url !== file)
							: defaultValue,
					);
					if (onFileDelete) {
						await onFileDelete(file);
					}
				}}
			/>
			{children}
			{errorType && <FileUploadErrortext>{errorType}</FileUploadErrortext>}
		</Wrapper>
	);
}

export default memo(FileUpload);
